// Admin app router. Match Core's adminMenu route structure cho `chat` group:
// /inbox + /inbox/canned-responses + /inbox/agents + /inbox/knowledge-base
// + /inbox/ai-training + /inbox/ai-logs + /inbox/channels + /inbox/config
// SaaS-specific (API Keys, Webhooks) đặt vào group `system` theo Core.

import { Suspense } from 'react';
// `useParams` gỡ 27/08/2026 cùng RedirectSupportToTicket — nó là chỗ dùng duy nhất.
import { Routes, Route, Navigate } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
import { useAuthStore } from './lib/authStore';
import { homeRouteFor } from './lib/homeRoute';
import { resolveApiBase } from './lib/apiBase';
import lazyWithReload from '@cnv/ui/utils/lazyWithReload';
// Vỏ bọc cho dãy tiện ích nền cuối tệp: <Suspense> chỉ đỡ lúc TẢI chunk, không đỡ lúc
// widget NÉM — mà ở đây cái tóm được là Sentry.ErrorBoundary bọc CẢ <App/> (main.jsx),
// nên một widget nền hỏng là thay sạch giao diện bằng trang "Đã có lỗi xảy ra".
import BackgroundWidget from '@cnv/ui/common/BackgroundWidget';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
// Đăng nhập bằng mã OTP qua SMS — đường riêng của Connect (docs/DANG_NHAP_OTP_SMS.md),
// Core + Work không có. Lazy vì phần lớn người dùng vào bằng email/mật khẩu.
const LoginOtpPage = lazyWithReload(() => import('./pages/LoginOtpPage'));
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
const SettingUpPage = lazyWithReload(() => import('./pages/SettingUpPage'));
const InvitationsPage = lazyWithReload(() => import('./pages/InvitationsPage'));
import AcceptInvitePage from './pages/AcceptInvitePage';
const WorkspaceSelectorPage = lazyWithReload(() => import('./pages/WorkspaceSelectorPage'));
// Public share (ADR-032) — KHÁCH HÀNG mở, không đăng nhập. Clone 1:1 từ Core
// attendance-app/admin/src/pages/public/ (chỉ đổi API base + import productUnit + branding footer).
const PublicQuotationPage = lazyWithReload(() => import('./pages/public/PublicQuotationPage'));
// Ảnh công trường (docs/JOBSITE_PHOTOS.md) — CHỈ có ở CNV Connect, drift cố ý (docs/PORT_GAPS.md).
// Trang xem công khai KHÔNG nằm trong RequireAuth: chủ nhà và thầu phụ không có tài khoản.
const PublicInvoicePage = lazyWithReload(() => import('./pages/public/PublicInvoicePage'));
// Khách duyệt báo giá dịch vụ hỗ trợ — trang kernel, bọc một lớp để truyền địa chỉ API (xem tệp).
const PublicTicketQuotePage = lazyWithReload(() => import('./pages/public/PublicTicketQuotePage'));
// Lịch làm việc chia sẻ bằng link (Xếp việc › Thao tác › Chia sẻ lịch — vòng 4, 20/09/2026). Tổ thợ,
// chủ nhà, thầu phụ mở KHÔNG đăng nhập. Trang kernel gọi `fetch` trần (không qua axios của hub —
// interceptor 401 đẩy về /login); địa chỉ API theo cùng luật hostname → `api.` như mọi trang khác
// của hub, truyền thẳng ở Route dưới (hub không có `/api` cùng origin — xem PublicTicketQuotePage).
const PublicSchedulePage = lazyWithReload(() => import('@cnv/ui/pages/projects/PublicSchedulePage'));
const PUBLIC_API_BASE = resolveApiBase();
// /inbox/channels — port 1:1 từ Core (cnv-ui), thay cho SaaS's ChannelsPage M1 tự bịa
// Kho ứng dụng dùng CHUNG một trang với cnvwork-saas (kernel). Trước đây Connect có bản
// viết tay 349 dòng — cùng ý tưởng nhưng thiếu thang bậc gói, lịch sử cấp phép, danh sách
// người đang giữ suất… nên trông như thế hệ cũ dù mới viết. Phần khác nhau giữa hai nền
// tảng (bảng giá, tên gói) nằm ở '@host/marketplaceBrand' của từng app.
const MarketplacePage = lazyWithReload(() => import('@cnv/ui/pages/marketplace/MarketplacePage'));

// Team Chat (Slack/Lark-style) — port từ Core attendance-app.
// Cross-module dep FulfillmentOrderDrawerContext đã stub (navigate full page khi
// chưa port fulfillment) — xem cnv-ui/src/components/fulfillment/FulfillmentOrderDrawerContext.jsx
const TeamChatPage = lazyWithReload(() => import('@cnv/ui/pages/team-chat/TeamChatPage'));

// Push notifications — re-export wrappers point tới @cnv/ui (share kernel).
// Match Core's main.jsx pattern. Trong CNV Work desktop (Electron), 3 component
// này detect window.cnvDesktop và route qua native macOS/Win/Linux noti thay
// vì Web Push (Push API unavailable trong Electron).
// ChatPushNotifications (noti tin Hộp thư khách hàng — Zalo/FB) GỠ 12/10/2026: Site Connect không bán Hộp thư; nó mở
// một socket `/inbox` thường trực cho MỌI phiên đăng nhập chỉ để chờ tin không bao giờ tới. Tệp shim
// ./components/chat/ChatPushNotifications GIỮ — bật lại = trả dòng lazy này + dòng BackgroundWidget cuối tệp.
const TeamChatPushNotifications = lazyWithReload(() => import('./components/chat/TeamChatPushNotifications'));
const WebPushRegister = lazyWithReload(() => import('./components/chat/WebPushRegister'));
const CommandPalette = lazyWithReload(() => import('@cnv/ui/common/CommandPalette'));
const WebVersionWatcher = lazyWithReload(() => import('@cnv/ui/common/WebVersionWatcher'));
const QuickScreenshotShortcut = lazyWithReload(() => import('@cnv/ui/feedback/QuickScreenshotShortcut'));
const HelpWidgetLoader = lazyWithReload(() => import('@cnv/ui/support-help/HelpWidgetLoader'));
// FloatingCallWidget (softphone CNV Call) GỠ 12/10/2026 cùng cụm CNV Call — Site Connect không bán tổng đài (menu +
// route đã bỏ từ 16/08). Tệp ./components/voip/* GIỮ.

// Inbox sub-pages — clone từ Core (cnv-ui)
const ChatAILogsPage = lazyWithReload(() => import('@cnv/ui/pages/inbox/ChatAILogsPage'));
// Phase D2: Inbox Reports (metrics + QC + ROI + performance)

// CRM Sales pages (M4 ADR-022 — internal-only) — clone từ Core (cnv-ui)

// CRM Finance + Promotion pages (M5 ADR-022 — internal-only) — clone từ Core (cnv-ui)
// CrmCouponsPage không import — Core route redirect /crm/coupons → /crm/promotions

// Phase B: Products + Categories
// Phase C1: CreditNotes
// Phase C2: DealRoles + Industries
// Phase C3: DocTemplates + Documents + DataTransfer + DuplicateCheck + RoundRobin
// Port từ Core 2026-05-12 (sync gap): Commissions + Revenue + ContractEnd report
const HrAttendanceReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportsPage'));
const HrAssetReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/AssetReportPage'));
const HrAssetAssignmentReportPage = lazyWithReload(() => import('@cnv/ui/pages/reports/AssetAssignmentReportPage'));
// Phase C3: Quotation Approval Settings (đã clone từ trước, em add wire)

// Reports Hub — top-level theo Core (adminMenu line 193-200), KHÔNG nested trong CRM.
const ReportsHub = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportsHub'));
const ReportRoute = lazyWithReload(() => import('@cnv/ui/pages/reports/ReportRoute'));

// Approvals (M6) — port từ Core
import ApprovalShell from '@cnv/ui/approvals/ApprovalShell';
const ApprovalInboxPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalInboxPage'));
const ApprovalTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalTemplatesPage'));
const ApprovalDetailPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalDetailPage'));
const TemplateBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/TemplateBuilderPage'));
const ApprovalReportPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalReportPage'));
const MyPendingPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/MyPendingPage'));
const MyRequestsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/MyRequestsPage'));
const SharedListsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/SharedListsPage'));
const ApprovalsPage = lazyWithReload(() => import('@cnv/ui/pages/approvals/ApprovalsPage'));

// Dashboard (M6) — port từ Core
const DashboardPage = lazyWithReload(() => import('@cnv/ui/pages/dashboard/DashboardPage'));

// HR (M6.2b) — Employees + Teams + OrgChart (port từ Core)
const EmployeesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/EmployeesPage'));
const EmployeeDetailPage = lazyWithReload(() => import('@cnv/ui/pages/employees/EmployeeDetailPage'));
// EmployeeEventsPage ('@cnv/ui/pages/employees/EventsPage') gỡ 27/08/2026 cùng mục "Sự kiện".

// TỔNG QUAN CÔNG TRÌNH (27/08/2026) — trang riêng của Connect, KHÔNG vào @cnv/ui.
// Cụm Việc bên kernel không có màn này; kernel hoá sau khi sếp duyệt bản xem trước.
const ProjectsOverviewPage = lazyWithReload(() => import('./pages/ProjectsOverviewPage'));
const ProjectProfilePage = lazyWithReload(() => import('./pages/ProjectProfilePage'));
const ProjectsBoardPage = lazyWithReload(() => import('./pages/ProjectsBoardPage'));
// Bản đồ nạp cả leaflet — TÁCH chunk riêng, đừng để trang danh sách gánh theo.
const ProjectsMapPage = lazyWithReload(() => import('./pages/ProjectsMapPage'));
// Bản đồ CHECK-IN (ai đang ở đâu — kernel CheckinMapReport). Từ 04/09/2026 có URL RIÊNG trở
// lại (sếp Phú: *"checkin là làm url riêng đi"*): 31/08 nó bị gộp thành một LỚP của
// /projects/map (?layer=checkin), nhưng một mục menu trỏ vào query string của mục menu khác
// thì bookmark khó đọc, nút back nhảy lẫn hai bản đồ, và không treo được đầu trang riêng.
// Đường cũ ?layer=checkin vẫn sống — kernel ProjectsMapPage tự chuyển hướng về đây.
const ProjectsCheckinMapPage = lazyWithReload(() => import('./pages/ProjectsCheckinMapPage'));
const ProjectsSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectsSettingsPage'));
// Circle — nhịp làm việc kiểu sprint. Route TĨNH nên React Router luôn thắng `/tasks/:id`.
const CirclesPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/CirclesPage'));
const ProjectTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectTemplatesPage'));
const ProjectFormTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectFormTemplatesPage'));
const ProjectFormTemplateEditPage = lazyWithReload(() => import('@cnv/ui/pages/projects/ProjectFormTemplateEditPage'));
const EmployeeCostRatesPage = lazyWithReload(() => import('./pages/EmployeeCostRatesPage'));
const AttendanceUnallocatedHoursPage = lazyWithReload(() => import('./pages/AttendanceUnallocatedHoursPage'));
// Giao diện Kanban — PORT từ cnvwork-saas 27/08/2026. Trang vốn ở cnv-ui (dùng chung ba hệ),
// chỉ Connect chưa mount nên bảng Kanban ở đây không theo chuẩn chung của hệ thống.
const KanbanThemePage = lazyWithReload(() => import('@cnv/ui/pages/settings/KanbanThemePage'));
// Menu app theo WORKSPACE (21/09/2026) — admin đặt menu chung, người dùng đổi trong phạm vi ở /me.
const MobileAppSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/MobileAppSettingsPage'));
// Bàn xếp việc — lưới tuần kéo thả (chunk C+D).
// Kernel hoá 03/09/2026 — bản host là bản CHUẨN được mang vào @cnv/ui (Work đã trôi 129 dòng);
// nay ba hệ cùng một tệp. Xem đầu tệp kernel.
const WorkSchedulePage = lazyWithReload(() => import('@cnv/ui/pages/projects/WorkSchedulePage'));
const OrgChartPage = lazyWithReload(() => import('@cnv/ui/pages/employees/OrgChartPage'));
const ContractsPage = lazyWithReload(() => import('@cnv/ui/pages/employees/ContractsPage'));
const ContractTypesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/ContractTypesPage'));
const JobTitlesPage = lazyWithReload(() => import('@cnv/ui/pages/employees/JobTitlesPage'));
const TeamsPage = lazyWithReload(() => import('@cnv/ui/pages/teams/TeamsPage'));

// HR Leaves + Holidays (M6.3) — đơn nghỉ phép + số dư phép + lịch nghỉ lễ
const LeavesPage = lazyWithReload(() => import('@cnv/ui/pages/leaves/LeavesPage'));
const LeaveBalancePage = lazyWithReload(() => import('@cnv/ui/pages/leaves/LeaveBalancePage'));
const HolidaysPage = lazyWithReload(() => import('@cnv/ui/pages/holidays/HolidaysPage'));

// HR Shifts (M6.4) — ca làm việc (prerequisite cho Attendance)
const ShiftsPage = lazyWithReload(() => import('@cnv/ui/pages/shifts/ShiftsPage'));
const TimesheetCreditRulesPage = lazyWithReload(() => import('@cnv/ui/pages/shifts/TimesheetCreditRulesPage'));

// HR Locations (M6.5) — geofencing check-in
const LocationsPage = lazyWithReload(() => import('@cnv/ui/pages/locations/LocationsPage'));

// Automation (M6.11) — flow builder + runs
const AutomationListPage = lazyWithReload(() => import('@cnv/ui/pages/automation/AutomationListPage'));
const AutomationBuilderPage = lazyWithReload(() => import('@cnv/ui/pages/automation/AutomationBuilderPage'));

// Settings (M6.12) — 14 pages: tenant config + RBAC + templates + integrations
const SettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/SettingsPage'));
const StorageUsagePage = lazyWithReload(() => import('@cnv/ui/pages/settings/StorageUsagePage'));
const NotificationLogsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/NotificationLogsPage'));
const RolesPage = lazyWithReload(() => import('@cnv/ui/pages/settings/RolesPage'));
const PermissionsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/PermissionsPage'));
const IntegrationChatGPTPage = lazyWithReload(() => import('@cnv/ui/pages/settings/IntegrationChatGPTPage'));
const IntegrationClaudePage = lazyWithReload(() => import('@cnv/ui/pages/settings/IntegrationClaudePage'));
// Microsoft 365 / Entra bỏ khỏi CNV Connect (2026-08-16) — Core + Work SaaS vẫn dùng.
const GoogleSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/GoogleSettingsPage'));
// Mattermost integration bỏ khỏi SaaS (2026-05-11) — Core attendance-app vẫn dùng.
const EmailTemplatesPage = lazyWithReload(() => import('@cnv/ui/pages/settings/email-templates/EmailTemplatesPage'));
// Fireflies integration bỏ khỏi SaaS (2026-07-11) — Core attendance-app vẫn dùng.
// CNV Chat integration bỏ khỏi SaaS (2026-07-25) — SaaS tự đăng ký chat-service. Core vẫn dùng.
const BotsPage = lazyWithReload(() => import('@cnv/ui/pages/settings/BotsPage'));
const BotDetailPage = lazyWithReload(() => import('@cnv/ui/pages/settings/BotDetailPage'));
// CNV Call (EtelecomSettingsPage) bỏ khỏi CNV Connect (2026-08-16) — Core + Work SaaS vẫn dùng.

// Payroll (M6.10) — 6 trang `/payroll/*` + `/profile/payroll` KHÔNG còn import (12/10/2026, sếp Phú: Site Connect không
// bán Lương) ⇒ chunk không vào bundle, `prefetchAllRoutes` không tải. Route chuyển về /dashboard ở dưới.

// Attendance (M6.9) — chấm công + corrections + penalty rules + timesheet
const AttendancePage = lazyWithReload(() => import('@cnv/ui/pages/attendance/AttendancePage'));
const CorrectionReviewPage = lazyWithReload(() => import('@cnv/ui/pages/attendance/CorrectionReviewPage'));
const MonthlyTimesheetPage = lazyWithReload(() => import('@cnv/ui/pages/timesheet/MonthlyTimesheetPage'));
const WeeklyTimesheetPage = lazyWithReload(() => import('@cnv/ui/pages/timesheet/WeeklyTimesheetPage'));
// LatePenaltyPage ('@cnv/ui/pages/penalties/LatePenaltyPage') gỡ 27/08/2026 cùng mục
// "Phạt đi trễ" — route /penalties nay là chuyển hướng.
// N6 — trang riêng của Connect (KHÔNG vào @cnv/ui: pages/attendance bên đó dùng chung 3 hệ).
const AttendancePolicyPage = lazyWithReload(() => import('./pages/AttendancePolicyPage'));
// M7 — lịch làm việc theo ngày (cùng lý do đặt ở admin của Connect, xem ADR-036).
const AttendanceRosterPage = lazyWithReload(() => import('./pages/AttendanceRosterPage'));

// Tích hợp Connecteam — kéo dữ liệu MỘT CHIỀU về. Riêng của Connect (một khách dùng), nên
// đặt ở admin này chứ không @cnv/ui: đưa vào gói chung là lan sang Core + Work SaaS.
const ConnecteamPage = lazyWithReload(() => import('./pages/ConnecteamPage'));

// Khách hàng (Customers 360°) — SaaS-only. URL /crm/customers khớp Core (parity 2026-06-05).

// Tiện ích — Nhà cung cấp + Kho in ấn
const SupplierListPage = lazyWithReload(() => import('@cnv/ui/pages/suppliers/SupplierListPage'));
// Export Center (Tiện ích → Xuất file) — docs/EXPORT_CENTER.md.
const ExportsPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/ExportsPage'));
const SupplierDetailPage = lazyWithReload(() => import('@cnv/ui/pages/suppliers/SupplierDetailPage'));
const PrintLibraryPage = lazyWithReload(() => import('@cnv/ui/pages/printing/PrintLibraryPage'));
const NotificationTemplateListPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/NotificationTemplateListPage'));
const NotificationTemplateDetailPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/NotificationTemplateDetailPage'));
const CompanyRoomPage = lazyWithReload(() => import('@cnv/ui/pages/utilities/CompanyRoomPage'));
const PrintItemDetailPage = lazyWithReload(() => import('@cnv/ui/pages/printing/PrintItemDetailPage'));

// Marketing (M6.8) — module BỎ khỏi CNV Connect, page vẫn còn trong cnv-ui (xem App.jsx
// khối chặn route + docs/PORT_GAPS.md). Không import ⇒ 11 chunk marketing không vào bundle.

// User profile + me settings — Core port (logout button trong AccountSection của /me)
const MeSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/me/MeSettingsPage'));
const ProfilePage = lazyWithReload(() => import('@cnv/ui/pages/profile/ProfilePage'));

// Assets (M6.6) — quản lý tài sản công ty
const AssetListPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssetListPage'));
const AssetDetailPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssetDetailPage'));
const AssetAssignmentPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AssignmentPage'));
const AssetAuditPage = lazyWithReload(() => import('@cnv/ui/pages/assets/AuditPage'));
const DigitalAssetsPage = lazyWithReload(() => import('@cnv/ui/pages/digital-assets/DigitalAssetsPage'));

// Recruitment (M6.7) — module BỎ khỏi CNV Connect. CHỈ giữ 3 page Pipeline: mục «Thư viện
// Pipeline» nằm ở menu Tiện ích và ticket dùng chung (xem docs/PORT_GAPS.md).
const PipelineSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/recruitment/PipelineSettingsPage'));

// Tasks (M6) — port từ Core
const TasksPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TasksPage'));
// TaskBoard, TaskWorkloadView, TemplatesPage — KHÔNG import standalone, render bên trong TasksPage qua ?view= query param (match Core)
const TaskDetailPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TaskDetailPage'));
const TaskStagesSettingsPage = lazyWithReload(() => import('@cnv/ui/pages/tasks/TaskStagesSettingsPage'));

// Support (M6) — 9 page port từ Core, TẮT 27/08/2026 (sếp Phú). Import lazy gỡ theo route:
// để lại thì lint đỏ "unused", mà giữ import cũng không đổi được gì — chunk chỉ tải khi route
// khớp. Bật lại thì khai lại đúng 9 dòng này từ '@cnv/ui/pages/support/*':
//   TicketsPage · TicketDetailPage · SupportDashboardPage · TicketCategoriesPage · TicketSLAPage
//   TicketAssignmentRulesPage · TicketCannedResponsesPage · TicketSettingsPage · WidgetBuilderPage

// Nơi đáp khi vào thẳng "/" (và mọi URL lạ — catch-all cuối file đẩy về "/"). Phải là
// COMPONENT chứ không phải <Navigate> tĩnh: đích phụ thuộc tenant đang hoạt động.
// `activeTenant` được authStore persist xuống localStorage nên có ngay lần render đầu; lúc
// chưa có (vừa đăng nhập, chưa chọn workspace) thì homeRouteFor trả nhánh dự phòng.
function HomeRedirect() {
  const activeTenant = useAuthStore((s) => s.activeTenant);
  return <Navigate to={homeRouteFor(activeTenant)} replace />;
}

// Cụm báo cáo tắt theo module đã bỏ. `module` ở đây là khoá trong REPORTS_CATALOG của
// cnv-ui — KHÔNG phải path. «Tuyển dụng» không có tab báo cáo nào nên không cần khai.
// Sửa danh sách này thì sửa cả AppLayout.jsx cho khớp.
//
// - crm / marketing / chat: 4 module bỏ khỏi CNV Connect (sếp Phú chốt 2026-08-13).
// - fulfillment («Triển khai», 14 thẻ) + work («Sức khoẻ khách hàng CNV Work», 1 thẻ):
//   thêm 2026-08-16. Hai cụm này KHÔNG chỉ là "chưa port" — chúng là báo cáo nội bộ của
//   CNV (đơn triển khai, ví credit, khách đang dùng CNV Work), Connect không có menu lẫn
//   module BE: `/api/v1/fulfillment/*` không mount ở api/src/server.js nên 14 thẻ Triển
//   khai đều rơi 404 route_not_found, còn thẻ CNV Work có Component:null nên chỉ ra màn
//   "đang được phát triển". Admin (permissions ['*']) qua được cửa `permission` của
//   catalog nên vẫn thấy đủ 15 thẻ chết — phải chặn ở tầng host này.
const HIDDEN_REPORT_MODULES = ['crm', 'marketing', 'chat', 'fulfillment', 'work'];

// Thẻ báo cáo ĐÃ CHUYỂN sang màn khác — ẩn khỏi Kho báo cáo để một trang chỉ có MỘT đường vào
// (sếp Phú 27/08/2026). Khác HIDDEN_REPORT_MODULES: kia là "cụm này Connect không bán", đây là
// "vẫn dùng, nhưng nằm chỗ khác".
//   · checkin-map → Dự án ▸ Bản đồ check-in (/projects/checkin-map)
const HIDDEN_REPORT_KEYS = ['checkin-map'];

// Mục bảng lệnh Cmd+K bị ẩn ở Connect. Khoá = `id` trong DEFAULT_COMMANDS của cnv-ui
// (CommandPalette.jsx) — lọc theo id để giữ nguyên bản dịch trong catalog của gói chung.
// Bảng lệnh là LỐI VÀO THỨ HAI, không đi qua menu: ẩn mục menu mà quên đây thì Cmd+K vẫn
// dẫn vào trang đã tắt, rơi vào chuyển hướng, trông như bấm hụt.
//   · inbox + crm-*  : 4 module bỏ khỏi Connect (2026-08-13)
//   · tickets        : cụm Hỗ trợ tắt 27/08/2026
//   · dashboard      : "Tổng quan" ẩn khỏi menu 27/08/2026 — trang vẫn sống làm đích rơi của
//                      guard, nhưng không mời người ta vào bằng lối tắt nữa.
const HIDDEN_COMMAND_IDS = [
  'inbox', 'crm-customers', 'crm-companies', 'crm-deals', 'crm-leads', 'crm-quotations',
  'crm-invoices', 'tickets', 'dashboard',
];

// RedirectSupportToTicket (/support/:id → /tickets/:id, anh chốt revert 2026-05-17) gỡ
// 27/08/2026 cùng cụm Hỗ trợ — cả hai họ path nay về thẳng /dashboard.

export default function App() {
  return (
    // Global Suspense cover 155 lazy() page (per share-kernel pattern Core).
    // Pre-auth (Login/Signup) eager nên KHÔNG cần Suspense bao quanh — Suspense
    // chỉ kick in khi render lazy component.
    <>
    <Suspense fallback={<div className="flex justify-center items-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
        <Route path="/login/otp" element={<LoginOtpPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* Quên mật khẩu — KHÔNG RequireAuth: người dùng vào đây CHÍNH VÌ không đăng nhập
          được. `/reset-password` là đích của link trong email (kernel `buildResetUrl` dựng
          `{PUBLIC_ADMIN_BASE_URL}/reset-password?token=…`) nên ĐỔI PATH = mọi email đã gửi
          đều chết. KHÔNG dùng lazy: người dùng đang gấp, đừng thêm 1 round-trip chunk. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setting-up" element={<SettingUpPage />} />
      {/* Public invite accept page — KHÔNG cần RequireAuth. NV click link từ email. */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      {/* Public share link (ADR-032) — KHÁCH HÀNG bấm link "Xem / In" nhận qua email/zalo.
          KHÔNG RequireAuth: trước đây rơi vào RequireAuth → khách thấy màn hình ĐĂNG NHẬP.
          Path giữ 1:1 Core (/q/:id, /inv/:id); `:id` ở SaaS là share token 32 hex thay vì
          prefix uuid của Core (chống dò + resolve tenant) — xem ADR-032. */}
      <Route path="/q/:id" element={<PublicQuotationPage />} />
      <Route path="/inv/:id" element={<PublicInvoicePage />} />
      {/* Khách duyệt BÁO GIÁ dịch vụ hỗ trợ — 1:1 Core. Link do `sendQuote` dựng từ origin của
          hub (`/bao-gia-ho-tro/:id?token=`); thiếu route này thì rơi vào catch-all → màn đăng
          nhập, token mất. Cụm Hỗ trợ ẩn nhưng route này GIỮ — xem docs/PORT_GAPS.md. */}
      <Route path="/bao-gia-ho-tro/:id" element={<PublicTicketQuotePage />} />
      {/* Lịch làm việc chia sẻ — link `/s/schedule/<token>` do máy chủ dựng (`url_path`), người nhận
          không có tài khoản ⇒ NGOÀI RequireAuth. Token lạ / thu hồi / hết hạn: trang tự báo. */}
      <Route path="/s/schedule/:token" element={<PublicSchedulePage apiBase={PUBLIC_API_BASE} />} />
      {/* Bộ ảnh công trường chia sẻ ra ngoài — khách mở bằng token, KHÔNG đăng nhập.
          Máy chủ đã gọt dữ liệu trước khi trả (mặc định xoá toạ độ, bỏ tên người chụp). */}      {/* Workspace selector — yêu cầu login nhưng KHÔNG mount AppLayout (chưa có active tenant). */}
      <Route path="/select-workspace" element={<WorkspaceSelectorPage />} />
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        {/* Đích mặc định = /chat (sếp Phú chốt 27/08/2026), dự phòng /tasks khi tenant tắt
            Chat nội bộ — luật chung ở lib/homeRoute.js, đừng viết lại đích ở đây. */}
        <Route index element={<HomeRedirect />} />
        {/* Admin manage invitations (mời nhân viên) */}
        <Route path="/users/invitations" element={<InvitationsPage />} />

        {/* Chat (Slack/Lark) — gated bằng tenant.private_modules.team_chat=true.
            ĐỪNG nhầm với «Inbox» (/inbox, hộp thư khách hàng) đã bỏ khỏi CNV Connect. */}
        <Route path="/chat" element={<TeamChatPage />} />
        <Route path="/chat/:roomId" element={<TeamChatPage />} />

        {/* ── MODULE BỎ KHỎI CNV CONNECT — chặn route (sếp Phú chốt 2026-08-13) ──
            Inbox /inbox · CRM /crm · Marketing /marketing · Tuyển dụng /recruitment.
            Menu đã gỡ ở AppLayout.jsx; 4 dòng dưới chặn cả người gõ URL / bookmark cũ /
            link trong email + noti cũ. Page FE và module BE GIỮ NGUYÊN — bật lại module =
            revert commit này, không phải port lại.
            Lý do + phạm vi: docs/PORT_GAPS.md.
            LƯU Ý thứ tự: các route CÒN SỐNG có tiền tố trùng phải khai TRƯỚC khối này —
            /settings/pipelines/* (Thư viện Pipeline, ở Tiện ích) không đứng dưới /recruitment
            nên an toàn; /settings/ai-usage cũng vậy. */}
        <Route path="/inbox/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/inbox" element={<Navigate to="/dashboard" replace />} />
        <Route path="/crm/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/crm" element={<Navigate to="/dashboard" replace />} />
        <Route path="/marketing/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/marketing" element={<Navigate to="/dashboard" replace />} />
        <Route path="/recruitment/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/recruitment" element={<Navigate to="/dashboard" replace />} />

        {/* Reports Hub — top-level (Core adminMenu line 193-200).
            URL: /reports (all tabs) | /reports/:module (filtered tab) | /reports/:module/:key (specific report).
            `hiddenModules` bỏ luôn tab + thẻ báo cáo của 4 module đã tắt: không lọc thì
            admin (permissions ['*']) vẫn thấy hơn 100 thẻ CRM/Marketing/chat bấm vào là
            trắng — kho báo cáo là MẶT SAU của chính module đó, tắt module thì tắt cả đây.
            Route riêng của /reports/{crm,marketing,chat}/* cũng gỡ theo (page đã unmount). */}
        <Route path="/reports" element={<ReportsHub hiddenModules={HIDDEN_REPORT_MODULES} hiddenKeys={HIDDEN_REPORT_KEYS} />} />
        <Route path="/reports/:module" element={<ReportsHub hiddenModules={HIDDEN_REPORT_MODULES} hiddenKeys={HIDDEN_REPORT_KEYS} />} />
        {/* Báo cáo có trang riêng (catalog Component:null) — mount TRƯỚC ReportRoute generic để parity Core (main.jsx). Page đều shared @cnv/ui. */}
        <Route path="/reports/hr/attendance" element={<HrAttendanceReportPage />} />
        <Route path="/reports/hr/assets" element={<HrAssetReportPage />} />
        <Route path="/reports/hr/asset-assignments" element={<HrAssetAssignmentReportPage />} />
        {/* Thẻ «Thống kê phê duyệt» trong hub có Component:null nên trước đây bấm vào ra màn
            "đang được phát triển" — trong khi trang thật ĐANG CHẠY ở /approvals/report (vào
            được qua secondary nav của Phê duyệt). Đẩy thẳng sang đó thay vì mount lại
            ApprovalReportPage ở đây: /approvals/report nằm trong FULLBLEED_PATTERNS của
            AppLayout, mount ở /reports/* sẽ mất layout full-bleed. */}
        <Route path="/reports/approval/overview" element={<Navigate to="/approvals/report" replace />} />
        <Route path="/reports/:module/:key" element={<ReportRoute hiddenModules={HIDDEN_REPORT_MODULES} hiddenKeys={HIDDEN_REPORT_KEYS} />} />

        {/* Dashboard (Tổng quan) — M6, match Core adminMenu key='dashboard' */}
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Công trình — tổng quan toàn bộ dự án (chunk A). Đặt TRƯỚC /employees vì không
            liên quan, chỉ là chỗ chèn gọn trong khối route đã đăng nhập. */}
        <Route path="/projects" element={<ProjectsOverviewPage />} />
        <Route path="/projects/board" element={<ProjectsBoardPage />} />
        <Route path="/projects/map" element={<ProjectsMapPage />} />
        <Route path="/projects/checkin-map" element={<ProjectsCheckinMapPage />} />
        {/* Lỗ lãi all-project GỠ 31/08/2026 (sếp Phú) — tiền xem trong hồ sơ từng dự án. */}
        <Route path="/projects/pnl" element={<Navigate to="/projects" replace />} />
        <Route path="/projects/settings" element={<ProjectsSettingsPage />} />
        {/* Khuôn dùng lại — dời từ cụm Việc sang 03/09/2026. `/tasks?view=templates` và
            `?view=form-templates` chuyển hướng về đây nên link cũ không chết. */}
        <Route path="/projects/templates" element={<ProjectTemplatesPage />} />
        <Route path="/projects/form-templates" element={<ProjectFormTemplatesPage />} />
        {/* Soạn/sửa biểu mẫu là TRANG RIÊNG, không popup (sếp Phú 03/09). `/new` phải đứng
            TRƯỚC `/:id` — không thì "new" bị nuốt làm id. */}
        <Route path="/projects/form-templates/new" element={<ProjectFormTemplateEditPage />} />
        <Route path="/projects/form-templates/:id" element={<ProjectFormTemplateEditPage />} />
        {/* Hồ sơ MỘT dự án — đặt SAU các path tĩnh của cụm: router rank tĩnh thắng :id,
            nhưng để cạnh nhau cho người đọc thấy cả cụm một chỗ. */}
        <Route path="/projects/:id" element={<ProjectProfilePage />} />
        <Route path="/employees/cost-rates" element={<EmployeeCostRatesPage />} />
        <Route path="/attendance/unallocated-hours" element={<AttendanceUnallocatedHoursPage />} />
        <Route path="/schedule" element={<WorkSchedulePage />} />

        {/* Nhân sự (Employees) — M6.2b shipped: BE + FE port từ Core 1:1 */}
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/:id" element={<EmployeeDetailPage />} />
        {/* "Sự kiện" ẨN 27/08/2026 (sếp Phú) — giữ NGUYÊN dòng route này dưới dạng chuyển
            hướng, KHÔNG xoá: xoá đi thì /employees/events tụt xuống khớp /employees/:id ở trên
            và mở trang chi tiết nhân viên với id="events" (màn lỗi, không phải 404). */}
        <Route path="/employees/events" element={<Navigate to="/dashboard" replace />} />
        <Route path="/teams" element={<TeamsPage />} />
        <Route path="/org-chart" element={<OrgChartPage />} />
        <Route path="/employees/contracts" element={<ContractsPage />} />
        <Route path="/employees/contract-types" element={<ContractTypesPage />} />
        <Route path="/employees/job-titles" element={<JobTitlesPage />} />
        {/* Lương TẮT 12/10/2026 (sếp Phú: Site Connect không bán Lương; menu đã ẩn từ trước, máy chủ chặn
            `/payroll/*` + `/salary-grades`). Link cũ / gõ tay về /dashboard như các cụm đã bỏ. */}
        {/* Sáu đường cũ khai TỪNG DÒNG (không gộp `/payroll/*`): tools/check-docs-ui-parity.js đối chiếu link trong
            tài liệu với `<Route path>` nguyên văn và bỏ qua mẫu có `*`. */}
        <Route path="/payroll/periods" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/periods/:id" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/structures" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/levels" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/insurance" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/tax" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/payroll" element={<Navigate to="/dashboard" replace />} />
        {/* M6.9 Attendance — full ship (BE + FE 6 pages) */}
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/timesheet" element={<WeeklyTimesheetPage />} />
        <Route path="/timesheet/monthly" element={<MonthlyTimesheetPage />} />
        <Route path="/shifts" element={<ShiftsPage />} />
        <Route path="/shifts/credit-rules" element={<TimesheetCreditRulesPage />} />
        <Route path="/corrections" element={<CorrectionReviewPage />} />
        {/* M6.3 Leaves + Holidays — full ship (BE + FE) */}
        <Route path="/leaves" element={<LeavesPage />} />
        <Route path="/leave-balance" element={<LeaveBalancePage />} />
        <Route path="/holidays" element={<HolidaysPage />} />
        {/* M6.6 Assets — full ship (5 pages) */}
        <Route path="/assets" element={<AssetListPage />} />
        <Route path="/assets/:id" element={<AssetDetailPage />} />
        <Route path="/assets/assignments" element={<AssetAssignmentPage />} />
        <Route path="/assets/audit" element={<AssetAuditPage />} />
        <Route path="/digital-assets" element={<DigitalAssetsPage view="list" />} />
        <Route path="/digital-assets/billing" element={<DigitalAssetsPage view="billing" />} />
        <Route path="/digital-assets/alerts" element={<DigitalAssetsPage view="alerts" />} />
        {/* Microsoft 365 / Entra: route bỏ khỏi CNV Connect (2026-08-16) — về /dashboard,
            chặn cả bookmark cũ lẫn người gõ tay URL. */}
        <Route path="/settings/integrations/microsoft" element={<Navigate to="/dashboard" replace />} />
        {/* "Phạt đi trễ" ẨN 27/08/2026 (sếp Phú) — menu bỏ ở AppLayout, route về /dashboard. */}
        <Route path="/penalties" element={<Navigate to="/dashboard" replace />} />
        <Route path="/attendance/policies" element={<AttendancePolicyPage />} />
        <Route path="/attendance/roster" element={<AttendanceRosterPage />} />
        <Route path="/locations" element={<LocationsPage />} />

        {/* Tiện ích — Nhà cung cấp + Kho in ấn */}
        <Route path="/utilities/exports" element={<ExportsPage />} />
        <Route path="/utilities/suppliers" element={<SupplierListPage />} />
        <Route path="/utilities/suppliers/:id" element={<SupplierDetailPage />} />
        <Route path="/suppliers" element={<SupplierListPage />} />
        <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
        <Route path="/utilities/print-library" element={<PrintLibraryPage />} />
        <Route path="/utilities/company-room" element={<CompanyRoomPage />} />
        <Route path="/utilities/notification-templates" element={<NotificationTemplateListPage />} />
        <Route path="/utilities/notification-templates/:id" element={<NotificationTemplateDetailPage />} />
        <Route path="/utilities/print-library/:id" element={<PrintItemDetailPage />} />
        {/* Alias /marketing/print-library/* GỠ cùng module Marketing — Kho in ấn nay chỉ
            còn canonical /utilities/print-library (không page nào trong cnv-ui còn navigate
            sang họ /marketing, đã grep). */}

        {/* Thư viện Pipeline — Core mount ở CẢ /recruitment/pipelines/* và
            /settings/pipelines/*. Module Tuyển dụng đã bỏ nên chỉ giữ họ /settings: page
            shared cnv-ui hardcode nav sang /settings/pipelines/* ở 6 chỗ
            (PipelineLibraryPage 61/134/152/179, PipelineBuilderPage 379/412, RecruitmentLayout 11,
            CampaignPipelineSelector 68, TicketDetailContent 3872), và mục menu ở Tiện ích cũng
            trỏ path này. Thiếu route = mọi thao tác tạo/sửa pipeline rơi vào catch-all "*". */}
        {/* Thư viện Pipeline TẮT 12/10/2026 — chỉ phục vụ Tuyển dụng / Deals / Ticket (đã tắt ở Site Connect). */}
        <Route path="/settings/pipelines" element={<Navigate to="/dashboard" replace />} />
        <Route path="/settings/pipelines/new" element={<Navigate to="/dashboard" replace />} />
        <Route path="/settings/pipelines/:id" element={<Navigate to="/dashboard" replace />} />

        {/* Automation — M6 wire single-link Placeholder */}
        {/* M6.11 Automation — full ship (BE + FE 2 pages, engine execute defer M7) */}
        <Route path="/automation" element={<AutomationListPage />} />
        <Route path="/automation/:id" element={<AutomationBuilderPage />} />

        {/* Finance «Tên miền» — trang giữ chỗ (Placeholder chữ Việt) của module Core chưa port, không có trong menu.
            12/10/2026 về /dashboard như các cụm đã bỏ. */}
        <Route path="/finance/domains" element={<Navigate to="/dashboard" replace />} />

        {/* Tasks (Công việc) — M6, match Core adminMenu key='tasks' */}
        {/* M6 Tasks — match Core route 1:1: TasksPage là unified với ?view=mine|templates|board|workload */}
        <Route path="/tasks/circles" element={<CirclesPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/tasks/my-tasks" element={<Navigate to="/tasks?view=mine" replace />} />
        <Route path="/tasks/projects" element={<Navigate to="/tasks?view=mine" replace />} />
        <Route path="/tasks/templates" element={<Navigate to="/tasks?view=templates" replace />} />
        {/* Danh mục biểu mẫu (22/08/2026). Sếp Phú: "nó phải là task/form-template mới đúng
            liên quan gì attandance đâu" — biểu mẫu thuộc cụm VIỆC, không phải nhân sự. */}
        <Route path="/tasks/form-template" element={<Navigate to="/tasks?view=form-templates" replace />} />
        <Route path="/tasks/form-templates" element={<Navigate to="/tasks?view=form-templates" replace />} />
        <Route path="/tasks/board" element={<Navigate to="/tasks?view=board" replace />} />
        <Route path="/tasks/workload" element={<Navigate to="/tasks?view=workload" replace />} />
        <Route path="/tasks/stages-settings" element={<TaskStagesSettingsPage />} />
        <Route path="/tasks/stages" element={<Navigate to="/tasks/stages-settings" replace />} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        {/* Approvals (Phê duyệt) — M6, match Core adminMenu key='approvals' */}
        {/* M6 Approvals — match Core 1:1: ApprovalShell wrapper với nested routes (tabs sidebar) */}
        <Route path="/approvals/templates/:id" element={<TemplateBuilderPage />} />
        <Route path="/approvals" element={<ApprovalShell />}>
          <Route index element={<ApprovalInboxPage />} />
          <Route path="inbox" element={<ApprovalInboxPage />} />
          <Route path="templates" element={<ApprovalTemplatesPage />} />
          <Route path="shared" element={<SharedListsPage />} />
          <Route path="report" element={<ApprovalReportPage />} />
        </Route>
        <Route path="/approvals/pending" element={<Navigate to="/approvals?tab=tasks" replace />} />
        <Route path="/approvals/my-requests" element={<Navigate to="/approvals?tab=sent" replace />} />
        <Route path="/approvals/legacy" element={<ApprovalsPage />} />
        <Route path="/approvals/legacy-pending" element={<MyPendingPage />} />
        <Route path="/approvals/legacy-my-requests" element={<MyRequestsPage />} />
        <Route path="/approvals/:id" element={<ApprovalDetailPage />} />

        {/* Hỗ trợ (Ticket / Help desk) — TẮT 27/08/2026, sếp Phú: "hiện tại chưa cần tính
            năng này". Cùng cách 4 module bỏ ở trên: route về /dashboard, page cnv-ui + module
            BE `support` + bảng DB giữ nguyên, bật lại = revert.
            Bookmark cũ (/tickets/:id trong email thông báo) rơi vào /tickets/* nên cũng về
            /dashboard chứ không ra trang trắng. Canonical /tickets/* chốt 2026-05-17; legacy
            /support/* trước đây chuyển sang /tickets/* — nay gộp chung một đích. */}
        <Route path="/tickets/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/tickets" element={<Navigate to="/dashboard" replace />} />
        <Route path="/support/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/support" element={<Navigate to="/dashboard" replace />} />

        {/* System group — SaaS-specific Tích hợp + Cài đặt */}
        {/* API Keys + Webhooks TẮT 12/10/2026 — scope/sự kiện đều thuộc hộp thư + CRM (đã tắt ở Site Connect). */}
        <Route path="/api-keys" element={<Navigate to="/dashboard" replace />} />
        <Route path="/webhooks" element={<Navigate to="/dashboard" replace />} />
        {/* Đường dẫn CHÍNH thức từ 29.08.2026: trang đã gom cả Billing vào nên "marketplace"
            (cửa hàng ứng dụng) không còn đúng việc nó làm. Giống hệt bên CNV Work SaaS. */}
        <Route path="/billing" element={<MarketplacePage />} />
        {/* Đường cũ GIỮ SỐNG, không xoá: link đã gửi cho khách và bookmark đều trỏ đường này. */}
        <Route path="/marketplace" element={<Navigate to="/billing" replace />} />
        {/* User profile + me settings (avatar click → /me, có logout button) */}
        <Route path="/me" element={<MeSettingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Phiếu lương của tôi — Lương tắt ở Site Connect (12/10/2026); lối tắt ở /me đã ẩn qua `features.payroll`. */}
        <Route path="/profile/payroll" element={<Navigate to="/dashboard" replace />} />
        {/* M6.12 Settings — 14 pages */}
        <Route path="/settings" element={<SettingsPage />} />
        {/* Trang BillingPage riêng của Connect đã nghỉ: trang kernel là bản BAO TRÙM — có đủ
            gói đang dùng, chỗ ngồi, dung lượng, thang bậc VÀ "Lịch sử gói" theo từng đợt mua. */}
        <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
        <Route path="/settings/storage" element={<StorageUsagePage />} />
        <Route path="/settings/email-templates" element={<EmailTemplatesPage />} />
        {/* :id = editor mở 1 mẫu (page điều hướng openEditor → /:id). Port 1:1 Core main.jsx:684.
            Thiếu route này → click mẫu unmount page, editor không mở. */}
        <Route path="/settings/email-templates/:id" element={<EmailTemplatesPage />} />
        {/* Mattermost routes bỏ khỏi SaaS (2026-05-11) */}
        <Route path="/settings/kanban-theme" element={<KanbanThemePage />} />
        <Route path="/settings/mobile-app" element={<MobileAppSettingsPage />} />
        <Route path="/settings/notification-logs" element={<NotificationLogsPage />} />
        <Route path="/settings/integrations/chatgpt" element={<IntegrationChatGPTPage />} />
        <Route path="/settings/integrations/claude" element={<IntegrationClaudePage />} />
        <Route path="/settings/integrations/connecteam" element={<ConnecteamPage />} />
        {/* Fireflies route bỏ khỏi SaaS (2026-07-11) */}
        {/* CNV Chat route bỏ khỏi SaaS (2026-07-25) */}
        <Route path="/settings/bots" element={<BotsPage />} />
        <Route path="/settings/bots/:id" element={<BotDetailPage />} />
        {/* CNV Call: route bỏ khỏi CNV Connect (2026-08-16) — về /dashboard. */}
        <Route path="/settings/integrations/cnv-call" element={<Navigate to="/dashboard" replace />} />
        {/* Chi phí AI — menu Hệ thống → trỏ qua ChatAILogsPage hiện đang ở /inbox/ai-logs */}
        <Route path="/settings/ai-usage" element={<ChatAILogsPage />} />
        <Route path="/settings/google" element={<GoogleSettingsPage />} />
        {/* Phân bổ lead (CRM) — không có trong menu, gọi toàn `/crm/*`. CRM tắt ở Site Connect (12/10/2026) ⇒ /dashboard. */}
        <Route path="/settings/lead-distribution" element={<Navigate to="/dashboard" replace />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/permissions" element={<PermissionsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>

    {/* Team-chat (/chat) push notifications — global STOMP subscribe, in-tab. */}
    <BackgroundWidget name="team-chat-push"><TeamChatPushNotifications /></BackgroundWidget>

    {/* Web Push — background noti khi tab đóng (service worker + VAPID). */}
    {/* Trong CNV Work desktop sẽ tự skip (Electron ko có push service). */}
    <BackgroundWidget name="web-push"><WebPushRegister /></BackgroundWidget>

    {/* Global command palette — Cmd+K / Ctrl+K mở quick nav (skip /chat). */}
    <BackgroundWidget name="command-palette"><CommandPalette hiddenIds={HIDDEN_COMMAND_IDS} /></BackgroundWidget>

    {/* Phím tắt chụp nhanh — Ctrl/Cmd+Shift+2 → chụp → annotate → tạo ticket / copy ảnh. */}
    <BackgroundWidget name="quick-screenshot"><QuickScreenshotShortcut /></BackgroundWidget>

    {/* Widget «Help» — chat / góp ý / báo lỗi / yêu cầu của workspace gửi đội hỗ trợ (nút + thông báo ở
        @cnv/ui support-help/HelpMenu — cụm dùng chung CNV Work từ 13/10/2026). */}
    <BackgroundWidget name="help-widget"><HelpWidgetLoader /></BackgroundWidget>

    {/* Phát hiện web deploy bản mới → banner tải lại + auto-reload khi đổi trang. */}
    <BackgroundWidget name="web-version-watcher"><WebVersionWatcher /></BackgroundWidget>
    </>
  );
}
